/**
 * Pagina Klanten, werkplek v6 (sessie 73, ontwerp 31).
 *
 * Laptop: cijferbalk over de volle breedte; daaronder lijst (320 px),
 * klantkaart (midden) en rechts (400 px) de tijdlijn boven en de
 * copiloot onder. Elk vlak scrolt apart. Zonder klant in de url opent de
 * bovenste klant van de lijst (`replace`, dus geen extra stap terug).
 *
 * iPhone (onder 900 px): de kaart vult het scherm; "Klanten" in de kop
 * toont de lijst, Tijdlijn en Copiloot schuiven als vlak omhoog met een
 * kruisje rechtsboven (dezelfde klassen als rechts op Vandaag).
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { v6Api } from "../api";
import { Icoon } from "../Icoon";
import "../Rechts/rechts.css";
import { v6Sleutels } from "../sleutels";
import { vertaalApiFout } from "@/lib/api";

import { KlantkaartV10 } from "../Rechts/kaart10/KlantkaartV10";
import { Cijferbalk } from "./Cijferbalk";
import { KlantCopiloot } from "./KlantCopiloot";
import { KlantenLijst } from "./KlantenLijst";
import { useSmal } from "./kaart/smal";
import "./klanten.css";
import { useKlantenLijst } from "./lijstQuery";
import { voornaamVan } from "./opmaak";
import { Tijdlijn } from "./Tijdlijn";
import "@/styles/statusbalk.css";

type Vlak = "tijdlijn" | "copiloot";

export function WerkplekV6Klanten() {
  const { klantId = null } = useParams<{ klantId: string }>();
  const navigate = useNavigate();
  const smal = useSmal();
  const [zoek, setZoek] = useState("");
  const [toonLijst, setToonLijst] = useState(false);
  const [vlak, setVlak] = useState<Vlak | null>(null);
  const lijst = useKlantenLijst(zoek);

  // Zonder klant in de url: de bovenste klant van de lijst.
  const eerste = lijst.data?.klanten[0]?.id ?? null;
  useEffect(() => {
    if (!klantId && eerste) navigate(`/klanten/${eerste}`, { replace: true });
  }, [klantId, eerste, navigate]);

  const kaart = useQuery({
    queryKey: v6Sleutels.klantKaart(klantId ?? ""),
    queryFn: () => v6Api.klantKaart(klantId as string),
    enabled: Boolean(klantId),
  });
  const kaartData = kaart.data && kaart.data.id === klantId ? kaart.data : undefined;

  const kies = useCallback(
    (id: string) => {
      setToonLijst(false);
      setVlak(null);
      if (id !== klantId) navigate(`/klanten/${id}`);
    },
    [klantId, navigate],
  );
  // Mail en WhatsApp zetten via de bestaande route een nieuwe zaak klaar
  // (bron klantpagina) en openen hem op Vandaag in dat kanaal. Er wordt
  // nooit iets verstuurd.
  const nieuw = useMutation({
    mutationFn: (kanaal: "mail" | "whatsapp") =>
      v6Api.nieuwBericht({ klant_id: klantId as string, kanaal, bron: "klantpagina" }),
    onSuccess: (uitkomst, kanaal) => {
      if (uitkomst.zaak_id) navigate(`/?zaak=${uitkomst.zaak_id}&vak=${kanaal}`);
    },
  });
  const sluit = useCallback(() => setVlak(null), []);
  // Na AVG-wissen: terug naar de lijst (op de iPhone de lijst in beeld).
  const naGewist = useCallback(() => {
    setVlak(null);
    setToonLijst(true);
    navigate("/klanten");
  }, [navigate]);
  // "Op de kaart" (Uit gesprekken): op de iPhone het copilootvlak openen;
  // op de laptop staat de copiloot er al.
  const naarCopiloot = useCallback(() => {
    if (smal) setVlak("copiloot");
  }, [smal]);

  // Escape sluit het vlak op de iPhone.
  useEffect(() => {
    if (!vlak) return;
    const toets = (e: KeyboardEvent) => {
      if (e.key === "Escape") sluit();
    };
    document.addEventListener("keydown", toets);
    return () => document.removeEventListener("keydown", toets);
  }, [vlak, sluit]);

  const sluitKnop = (
    <button type="button" className="sluitvlak" aria-label="Sluiten" title="Sluiten" onClick={sluit}>
      <Icoon naam="x" klein />
    </button>
  );

  let midden;
  if (!klantId) {
    midden = (
      <p className="v6-leeg">
        {lijst.isLoading
          ? "Laden"
          : lijst.isError
            ? "Laden mislukt"
            : "Geen klant"}
      </p>
    );
  } else if (kaart.isError) {
    midden = (
      <p className="v6-fout kl-marge" role="alert">
        Laden mislukt
      </p>
    );
  } else if (!kaartData) {
    midden = <p className="v6-leeg">Laden</p>;
  } else {
    // Motor 7 golf 2 sessie E: dezelfde klantkaart v10 als in de zaak.
    midden = (
      <KlantkaartV10
        key={kaartData.id}
        kaart={kaartData}
        acties={{ nieuw: (kanaal) => nieuw.mutate(kanaal), bezig: nieuw.isPending }}
        tussen={
          nieuw.isError ? (
            <p className="v6-fout kl-marge" role="alert">
              Kon geen bericht klaarzetten: {vertaalApiFout(nieuw.error)}
            </p>
          ) : null
        }
        onGewist={naGewist}
        onCopiloot={naarCopiloot}
      />
    );
  }

  return (
    <div className={`kl-pagina${toonLijst ? " toon-lijst" : ""}`}>
      <Cijferbalk />
      <div className="kl-raam">
        <section className="v6-vlak kl-lijst" aria-label="Klantenlijst">
          <KlantenLijst gekozen={klantId} onKies={kies} zoek={zoek} onZoek={setZoek} />
        </section>

        <section className="v6-vlak kl-midden" aria-label="Klantkaart">
          <div className="kl-mobkop vo-statusbalkvrij">
            <button type="button" className="terug" onClick={() => setToonLijst(true)}>
              <Icoon naam="terug" klein />
              Klanten
            </button>
            <span className="r">
              <button
                type="button"
                disabled={!klantId}
                onClick={() => setVlak("tijdlijn")}
              >
                Tijdlijn
              </button>
              <button
                type="button"
                className="copi"
                disabled={!klantId}
                onClick={() => setVlak("copiloot")}
              >
                <Icoon naam="copiloot" klein />
                Copiloot
              </button>
            </span>
          </div>
          <div className="kl-kaart-scrol">{midden}</div>
        </section>

        {vlak ? (
          <div className="v6-rechts-overlay" data-testid="klanten-overlay" onClick={sluit} />
        ) : null}
        <aside
          className={`v6-rechts kl-rechts${vlak ? " toon" : ""}`}
          data-tab={vlak ?? undefined}
          aria-label="Tijdlijn en copiloot"
        >
          <section
            className="v6-vlak kl-tijdlijnvlak"
            aria-label="Tijdlijn"
            aria-hidden={(smal && vlak !== "tijdlijn") || undefined}
          >
            {klantId ? (
              <Tijdlijn
                key={klantId}
                klantId={klantId}
                voornaam={kaartData ? voornaamVan(kaartData.naam) : null}
                sluitKnop={sluitKnop}
              />
            ) : null}
          </section>
          <section
            className="v6-vlak v6-copivlak kl-copivlak"
            aria-label="Copiloot"
            aria-hidden={(smal && vlak !== "copiloot") || undefined}
          >
            {klantId ? <KlantCopiloot klantId={klantId} sluitKnop={sluitKnop} /> : null}
          </section>
        </aside>
      </div>
    </div>
  );
}
